import React, { useState } from 'react';

import { MtxIcon } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useGetApiDocument } from '@/api/document/document';
import { Spinner } from '@/components/Spinner/Spinner';
import { ROUTE } from '@/constants/route';
import { DOCUMENTS } from '@/enums/documents';
import { useDemoContext } from '@/hooks/DemoContext';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import base64ToBlob from '@/utils/functions/base64ToBlob/base64ToBlob';
import { log } from '@/utils/functions/logger/logger';

import styles from './styles.module.css';

export type DocumentsRowProps = {
  typeDocument: string | null | undefined;
  dateDocument: string | null | undefined;
  token: string | null | undefined;
  millesime: string | null | undefined;
};

export const DocumentsRow: React.FC<DocumentsRowProps> = ({ typeDocument, dateDocument, token, millesime }) => {
  const { isDemo } = useDemoContext();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const { refetch, data: cachedData } = useGetApiDocument(token ?? '', {
    query: {
      enabled: false,
      staleTime: Infinity,
      gcTime: 1000 * 60 * 15,
    },
  });

  const openDocument = (body: string) => {
    const url = URL.createObjectURL(base64ToBlob(body, 'application/pdf'));

    globalThis.open(url, '_blank', 'noopener noreferrer');

    setTimeout(() => {
      globalThis.URL.revokeObjectURL(url);
    }, 1000);
  };

  const handleDocumentClick = async () => {
    if (isDemo) return;

    setIsLoading(true);
    // Use cached data if available
    if (cachedData?.body) {
      openDocument(cachedData.body);
      setIsLoading(false);
      return;
    }

    // Otherwise fetch the document
    try {
      const { data } = await refetch();

      if (data?.body) {
        openDocument(data.body);
      }
    } catch (error) {
      log.error(`Erreur lors de la récupération du document`, error);
      navigate(ROUTE.ERREUR, { state: { goTo: ROUTE.CONTRATS } });
    } finally {
      setIsLoading(false);
    }
  };

  const getDocumentLabel = () => {
    if (typeDocument === DOCUMENTS.REPORT_FREE_PAYMENT) {
      return t('documents.paymentReport_one');
    }
    if (typeDocument === DOCUMENTS.REPORT_REGULAR_ADVISORY) {
      return t('documents.regularReport_one', { millesime });
    }
    if (typeDocument === DOCUMENTS.REPORT_EXPOST) {
      return t('documents.exPostReport_one', { millesime });
    }
    return typeDocument;
  };

  return (
    <tr key={token} className={styles.documentRow}>
      <td>
        <p className={styles.documentType}> {getDocumentLabel()}</p>
        <p className={styles.documentDate}>
          {' '}
          {t('common.masculineDeterminer')}{' '}
          {dateDocument &&
            new Date(dateDocument).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })}
        </p>
      </td>
      <td>
        <p>
          {isLoading ? (
            <Spinner diameter={18} />
          ) : (
            <button
              type="button"
              className={styles.eyeOpenDocument}
              disabled={isDemo}
              onClick={() => {
                handleDocumentClick();
                void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                  event_id: 'clic',
                  event_categorie: 'clic',
                  event_metier: 'espace client placement',
                  event_nom: 'voir bulletin',
                  event_detail_1: 'picto',
                });
              }}
            >
              <MtxIcon name="eye" size={18} />
            </button>
          )}
        </p>
      </td>
    </tr>
  );
};
